import { useRouter } from "@core/Api/useRouter";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import type { GesCloudHandoffRefusalReason, OrganizationListItem } from "@ext/enterprise-cloud/GesCloudApi";
import { useGesCloudOrganizationStore } from "@ext/enterprise-cloud/ui-logic/stores/GesCloudOrganizationStore/GesCloudOrganizationStore.provider";
import t from "@ext/localization/locale/translate";
import { span, traced } from "@ext/loggers/opentelemetry";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogIcon,
	AlertDialogTitle,
} from "@ui-kit/AlertDialog";
import { Button, LoadingButtonTemplate } from "@ui-kit/Button";
import { useState } from "react";
import { isOrganizationSwitchRefusal, OrganizationSwitchRefusalModal } from "./OrganizationSwitchRefusalModal";
import useSwitchOrganization from "./useSwitchOrganization";

const SPAN_NAME = "ges-cloud-link-organization-switch";

interface SwitchConfirmProps {
	organizationId: string;
	organization?: OrganizationListItem;
	onRefused: (reason: GesCloudHandoffRefusalReason) => void;
}

const SwitchConfirm = ({ organizationId, organization, onRefused }: SwitchConfirmProps) => {
	const { switchOrganization, isSwitching } = useSwitchOrganization();

	const confirm = () =>
		traced(SPAN_NAME, async () => {
			span()?.addEvent("confirm", { organizationId });
			const result = await switchOrganization(organization, () => ModalToOpenService.resetValue());
			span()?.addEvent(result);
			if (isOrganizationSwitchRefusal(result)) onRefused(result);
		});

	return (
		<AlertDialogContent status="info">
			<AlertDialogHeader>
				<AlertDialogIcon icon="building" />
				<AlertDialogTitle>{t("enterprise-cloud.link-organization-switch.title")}</AlertDialogTitle>
				<AlertDialogDescription>
					{t("enterprise-cloud.link-organization-switch.message").replace(
						"{{name}}",
						organization?.organizationInfo.name ?? organizationId,
					)}
				</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel variant="outline">{t("cancel")}</AlertDialogCancel>
				{organization && !isSwitching ? (
					<Button onClick={() => void confirm()} variant="primary">
						{t("enterprise-cloud.link-organization-switch.action")}
					</Button>
				) : (
					<LoadingButtonTemplate
						text={t("enterprise-cloud.link-organization-switch.action")}
						variant="primary"
					/>
				)}
			</AlertDialogFooter>
		</AlertDialogContent>
	);
};

const OrganizationsLoadError = () => (
	<AlertDialogContent status="warning">
		<AlertDialogHeader>
			<AlertDialogIcon icon="alert-circle" />
			<AlertDialogTitle>{t("enterprise-cloud.link-organization-switch.title")}</AlertDialogTitle>
			<AlertDialogDescription>{t("enterprise-cloud.link-organization-switch.load-error")}</AlertDialogDescription>
		</AlertDialogHeader>
		<AlertDialogFooter>
			<AlertDialogCancel variant="outline">{t("close")}</AlertDialogCancel>
		</AlertDialogFooter>
	</AlertDialogContent>
);

interface GesCloudLinkOrganizationSwitchProps {
	organizationId: string;
}

const GesCloudLinkOrganizationSwitch = ({ organizationId }: GesCloudLinkOrganizationSwitchProps) => {
	const { organization, isLoading, error } = useGesCloudOrganizationStore((state) => ({
		organization: state.organizations.find(
			(item) => item.type === "organization" && item.organizationInfo.id === organizationId,
		),
		isLoading: state.isLoading,
		error: state.error,
	}));
	const [refusal, setRefusal] = useState<GesCloudHandoffRefusalReason | null>(null);
	const router = useRouter();

	// The link stays in the address so the refresh after a switch opens it; left there, every refresh would ask again.
	const cancel = () =>
		traced(SPAN_NAME, () => {
			span()?.addEvent("cancel", { organizationId });
			ModalToOpenService.resetValue();
			void router.pushPath("/");
		});

	const shownRefusal = refusal ?? (!organization && !isLoading && !error ? "forbidden" : null);
	if (shownRefusal) return <OrganizationSwitchRefusalModal onClose={cancel} reason={shownRefusal} />;

	return (
		<AlertDialog onOpenChange={(open) => !open && cancel()} open>
			{organization || isLoading ? (
				<SwitchConfirm onRefused={setRefusal} organization={organization} organizationId={organizationId} />
			) : (
				<OrganizationsLoadError />
			)}
		</AlertDialog>
	);
};

export default GesCloudLinkOrganizationSwitch;
